import React, { useRef, useState, useEffect } from 'react'
import { Field, FieldProps } from 'formik'

type OtpCodeInputProps = {
  name: string
  length?: number
  disabled?: boolean
}

export const OtpCodeInput: React.FC<OtpCodeInputProps> = ({ name, length = 6, disabled }) => {
  return (
    <Field name={name}>
      {(fieldProps: FieldProps) => <OtpInputInner {...fieldProps} length={length} disabled={disabled} />}
    </Field>
  )
}

const OtpInputInner: React.FC<FieldProps & { length: number; disabled?: boolean }> = ({
  field,
  form,
  length,
  disabled,
}) => {
  const { name, value } = field
  const { setFieldValue, errors, touched } = form
  const inputsRef = useRef<HTMLInputElement[]>([])
  const [localValues, setLocalValues] = useState<string[]>(new Array(length).fill(''))

  const hasError = touched[name] && errors[name]

  useEffect(() => {
    const formikValue = value || ''
    if (formikValue === '') {
      setLocalValues(new Array(length).fill(''))
      // 只有當真的被清空且不是初始狀態時才 focus
      if (document.activeElement?.tagName === 'INPUT') {
        inputsRef.current[0]?.focus()
      }
    } else if (formikValue.length === length && formikValue !== localValues.join('')) {
      setLocalValues(formikValue.split(''))
    }
    // effect 內會 setLocalValues，列入依賴會造成無限循環
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, length])

  const handleStateSync = (newValues: string[]) => {
    setLocalValues(newValues)
    setFieldValue(name, newValues.join(''))
  }

  const handleChange = (val: string, index: number) => {
    const singleDigit = val.slice(-1)
    if (singleDigit !== '' && !/^\d$/.test(singleDigit)) return

    const newValues = [...localValues]
    newValues[index] = singleDigit
    handleStateSync(newValues)

    if (singleDigit !== '' && index < length - 1) {
      inputsRef.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      const newValues = [...localValues]
      if (newValues[index]) {
        newValues[index] = ''
        handleStateSync(newValues)
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus()
        newValues[index - 1] = ''
        handleStateSync(newValues)
      }
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const paste = e.clipboardData.getData('text').trim()
    if (/^\d+$/.test(paste)) {
      const digits = paste.slice(0, length).split('')
      const newValues = [...digits, ...new Array(length - digits.length).fill('')]
      handleStateSync(newValues)
      const nextFocusIndex = Math.min(digits.length, length - 1)
      inputsRef.current[nextFocusIndex]?.focus()
    }
  }

  return (
    <div className="d-flex justify-content-center" style={{ gap: '12px' }}>
      {localValues.map((val, i) => (
        <input
          key={i}
          name={`${name}-${i}`}
          ref={el => (inputsRef.current[i] = el!)}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          disabled={disabled}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          value={val}
          onChange={e => handleChange(e.target.value, i)}
          onKeyDown={e => handleKeyDown(e, i)}
          onPaste={handlePaste}
          className={`form-control text-center rounded ${hasError ? 'border-danger' : 'border'}`}
          style={{
            width: '45px',
            height: '55px',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            backgroundColor: disabled ? '#e9ecef' : hasError ? '#fff8f8' : '#fff',
            transition: 'all 0.2s',
          }}
        />
      ))}
    </div>
  )
}
